import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { api, formatApiError } from '../lib/api'
import { track } from '../lib/analytics'
import { useAuth } from '../lib/auth'
import type { TokenResponse } from '../lib/types'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Card, CardBody } from '../components/ui/Card'
import { Logo } from '../components/Logo'
import { WaitlistLink } from '../components/marketing/WaitlistForm'
import { useTheme } from '../lib/theme'
import { Seo } from '../components/Seo'

export const seoTitle = 'Log in'
export const seoDescription =
  'Log in to your RemindLoop account to manage WhatsApp and SMS invoice reminders.'

export default function Login() {
  const { user, login } = useAuth()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  if (user) return <Navigate to="/dashboard" replace />

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      const token = await api.post<TokenResponse>('/api/v1/auth/login', { email, password })
      await login(token.access_token)
      track('login')
      navigate('/dashboard')
    } catch (err) {
      setError(formatApiError(err, 'Could not log in'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <Seo title={seoTitle} description={seoDescription} path="/login" />
      <div className="w-full max-w-sm">
        <div className="mb-6 flex justify-center">
          <Logo size={34} reversed={theme === 'dark'} />
        </div>
        <Card>
          <CardBody className="space-y-4">
            <h1 className="text-h1 text-foreground">Log in</h1>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                id="email"
                type="email"
                label="Email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                id="password"
                type="password"
                label="Password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {error && <p role="alert" className="text-sm text-danger-600">{error}</p>}
              <Button type="submit" className="w-full" isLoading={isSubmitting}>
                Log in
              </Button>
            </form>
          </CardBody>
        </Card>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          <Link
            to="/forgot"
            className="inline-flex min-h-11 items-center font-medium text-brand-700 hover:underline"
          >
            Forgot your password?
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Registration is by invitation.{' '}
          <WaitlistLink source="login" className="font-medium text-brand-700 hover:underline">
            Join the waitlist
          </WaitlistLink>
        </p>
      </div>
    </div>
  )
}
